/**
 * @file VideoHeader.js
 * @author Jonas Matulis, Simon Tenedero, Yevheniia Bazhmaieva
 * @created 2024-XX-XX
 * @lastModified 2025-06-01
 * @description file containing VideoHeader component
 */

import React, { useState, useEffect } from "react";
import {
  fetchActiveStreams,
  addLiveStream,
  getVideoFromFirebase,
} from "../../utils/livestreamsUtils";
import { logWebsiteUsage } from "../../utils/usersUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { PlayIcon } from "@heroicons/react/24/solid";
import { useUser } from "../../context/UserContext";
import { extractVideoIdFromUrl } from "../../utils/livestreamsUtils";

/**
 * bar to load videos by URL, shows history of loaded videos
 *
 * @param {*} props - The component properties
 * @param {string} props.videoUrl - The current video URL.
 * @param {Function} props.setVideoUrl - Function to set the video URL.
 *
 * @returns {JSX.Element} The rendered VideoHeader component.
 */
export const VideoHeader = ({ videoUrl, setVideoUrl }) => {
  const [history, setHistory] = useState([]); // State to store video history
  const [error, setError] = useState(""); // State to store error messages
  const navigate = useNavigate(); // Hook for navigation
  const location = useLocation(); // Hook for location information

  const [isInputFocused, setIsInputFocused] = useState(false);
  const { user, loading } = useUser();

  useEffect(() => {
    if (loading || !user) {
      return; // Do not run if user is not loaded or loading
    }

    // Load video history from loc
    // al storage and fetch active streams on component mount
    const savedHistory = JSON.parse(localStorage.getItem("videoHistory")) || [];
    setHistory(savedHistory);
    fetchActiveStreams().then(setHistory);

    //render video on component mounting
    window.location.pathname == "/load-live" && loadVideo(videoUrl);
  }, [user, loading]);

  /**
   * Handles form submission by loading the video based on the current video URL.
   *
   * @param {React.FormEvent<HTMLFormElement>} e - The form event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    await loadVideo(videoUrl);
  };

  /**
   * Gets video details either from firebase cache or oEmbed API as fallback
   *
   * @param {string} videoId - The video ID to get details for
   * @returns {Object} Video details object with title and source
   */
  const getVideoDetails = async (videoId) => {
    try {
      console.log(`Checking firebase for video ID: ${videoId}`);
      const cachedVideo = await getVideoFromFirebase(videoId);

      // Use cached title if it exists and is not a placeholder
      if (cachedVideo && cachedVideo.title) {
        console.log("cache title=", cachedVideo.title);
        if (!/^Video\s[\w-]{6,}$/i.test(cachedVideo.title)) {
          console.log(`Found in firebase: ${cachedVideo.title}`);
          return {
            title: cachedVideo.title,
            source: "firebase",
          };
        }
      }

      console.log("Not in Firebase, fetching from oEmbed API");

      // Get title from oEmbed
      const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;

      const response = await fetch(oEmbedUrl);

      if (response.ok) {
        const data = await response.json();

        if (data.title) {
          console.log(`Fetched from oEmbed: ${data.title}`);
          return {
            title: data.title,
            source: "oembed",
          };
        }
      }

      // Fallback if oEmbed fails
      console.log("oEmbed failed, using fallback");
      return {
        title: `Video ${videoId}`,
        source: "fallback",
      };
    } catch (error) {
      console.error("Error getting video details:", error);
      return {
        title: `Video ${videoId}`,
        source: "error",
      };
    }
  };

  /**
   * Loads video details based on the provided URL, updates history, and navigates to the load-live route.
   *
   * @param {string} url - The URL of the video to be loaded.
   */
  const loadVideo = async (url) => {
    const newVideoId = extractVideoIdFromUrl(url);

    if (newVideoId) {
      const videoDetails = await getVideoDetails(newVideoId, url);
      const title = videoDetails.title;

      localStorage.setItem("lastVideoUrl", url);
      updateHistory(title, url);

      await addLiveStream(newVideoId, title, url); //setDoc() in addLiveStream() handles duplicates if video has been added before
      fetchActiveStreams().then(setHistory);
      setError("");

      if (window.location !== "/load-live") {
        console.log("navigating back to load-live");

        //when loading a video, navigate to load-live page (render video)
        navigate("/load-live");
      }

      if (user) {
        logWebsiteUsage(
          user.uid,
          `User loaded video "${title}" with URL "${url}".`,
          "videoHistory",
        );
      }
      /*console.log(
        `Video loaded - Source: ${videoDetails.source}, Title: ${title}`,
      );*/
    } else {
      setError("Invalid YouTube URL");
    }
  };

  /**
   * Handles the click event on a history item, updating the video URL and loading the selected video.
   *
   * @param {string} url - The URL of the selected video.
   */
  const handleHistoryClick = async (url) => {
    setVideoUrl(url);
    await loadVideo(url);
  };

  /**
   * Updates the video history with the new video title and URL.
   *
   * @param {string} title - The title of the video.
   * @param {string} url - The URL of the video.
   */
  const updateHistory = (title, url) => {
    const newHistory = [
      { title, url },
      ...history.filter((item) => item.url !== url),
    ];
    setHistory(newHistory);
    localStorage.setItem("videoHistory", JSON.stringify(newHistory));
  };

  /**
   * Clears the video history from both state and local storage.
   */
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem("videoHistory");
  };

  /**
   * make video header not appear on admin panel
   */
  if (!user || location.pathname === "/adminPanel") {
    return <div></div>;
  }

  return (
    <div className="w-full items-center justify-center bg-transparent">
      {/* Form for submitting a YouTube URL */}
      <form onSubmit={handleSubmit} className="relative justify-center w-full">
        <div className="flex items-center w-full rounded-lg overflow-hidden">
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setTimeout(() => setIsInputFocused(false), 200)} // Delay to allow click on dropdown
            placeholder="Enter YouTube URL"
            className="h-7 w-11/12 px-4 text-sm text-left text-zinc-400 focus:outline-none bg-white rounded-l-xl"
            style={{ boxShadow: "inset 0 6px 16px rgba(0, 0, 0, 0.2)" }}
          />

          <button
            type="submit"
            className="px-4 py-1 h-7 bg-white mr-2 my-1 focus:outline-none rounded-r-xl"
          >
            <PlayIcon className="w-5 h-5 text-gray-600 hover:text-gray-900 bg-transparent" />
          </button>

          {isInputFocused && history.length > 0 && (
            <div className="absolute top-full mt-1 bg-gray-200 rounded-md shadow-lg border border-gray-300 max-h-60 overflow-y-scroll z-50 w-11/12">
              {history.map((item, index) => (
                <div
                  key={index}
                  onMouseDown={() => handleHistoryClick(item.url)}
                  className="flex justify-between items-center px-4 py-2 cursor-pointer hover:bg-gray-300 hover:text-black text-black"
                >
                  <span className="flex-grow truncate pr-2">{item.title}</span>

                  <span
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    className="text-red-500 hover:text-red-600 text-xs cursor-pointer flex-shrink-0"
                  >
                    delete
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
      {/* Display error message if there is an error */}
      {error && (
        <span className="absolute bottom-0 ml-3 text-xs text-red-500">
          {error}
        </span>
      )}
    </div>
  );
};

export default VideoHeader;
